import React, { useEffect, useState } from 'react';

type FormData = {
  name: string;
  email: string;
  phone: string;
  role: string;
  team?: string;
  emergencyContact?: string;
  emergencyRelation?: string;
  codigoComercial?: string;
  profileImageFile: File | null;
  profileImagePreview: string | null;
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onCreate: (data: FormData) => Promise<void>;
  loading?: boolean;
  error?: string | null;
  success?: string | null;
  initial?: Partial<FormData>;
  departmentLabel?: string;
  teamOptions?: string[];
  forcedTeam?: string;
  roleOptions?: string[];
  hideCodigoComercial?: boolean;
};

const defaultForm: FormData = {
  name: '',
  email: '',
  phone: '',
  role: '',
  team: '',
  emergencyContact: '',
  emergencyRelation: '',
  codigoComercial: '',
  profileImageFile: null,
  profileImagePreview: null
};

const ComercialCreateUserModal: React.FC<Props> = ({
  visible,
  onClose,
  onCreate,
  loading = false,
  error = null,
  success = null,
  initial,
  departmentLabel = 'Comercial',
  teamOptions = ['A', 'B', 'C'],
  forcedTeam,
  roleOptions = ['Gerente', 'Gestor', 'Assistente de Gestor', 'Instrutor', 'Assistente instrutor', 'Comercial'],
  hideCodigoComercial = false,
}) => {
  const [form, setForm] = useState<FormData>({
    ...defaultForm,
    team: forcedTeam || defaultForm.team,
    ...(initial || {}),
    ...(forcedTeam ? { team: forcedTeam } : {}),
  });

  useEffect(() => {
    if (visible) {
      setForm({
        ...defaultForm,
        team: forcedTeam || defaultForm.team,
        ...(initial || {}),
        ...(forcedTeam ? { team: forcedTeam } : {}),
      });
    }
  }, [visible, initial, forcedTeam]);

  if (!visible) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onCreate(form);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (!file) {
      setForm(prev => ({ ...prev, profileImageFile: null, profileImagePreview: null }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm(prev => ({ ...prev, profileImageFile: file, profileImagePreview: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backdropFilter: 'blur(2px)', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }} onClick={onClose}>
      <div style={{ width: '94%', maxWidth: 760, background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(0,0,0,0.06)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: 24, background: 'linear-gradient(135deg, #ef4444, #dc2626)', color: '#ffffff', position: 'relative' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(1200px 300px at -10% -20%, rgba(255,255,255,0.15), transparent)', pointerEvents: 'none' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: 0.2, color: '#ffffff' }}>Novo Utilizador</h2>
                <span style={{ background: 'rgba(255,255,255,0.18)', color: '#ffffff', padding: '4px 10px', borderRadius: 999, fontSize: 12 }}>
                  {departmentLabel}
                </span>
              </div>
              <div style={{ marginTop: 6, fontSize: 13, opacity: 0.95 }}>Preencha os dados do novo Utilizador</div>
            </div>
            <button aria-label="Fechar" onClick={onClose} style={{ background: 'rgba(255,255,255,0.18)', border: 0, color: '#ffffff', fontSize: 22, cursor: 'pointer', width: 36, height: 36, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          </div>
        </div>

        <div style={{ padding: 22 }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {error && (
              <div style={{ background: '#fee', border: '1px solid #fcc', color: '#b91c1c', padding: 12, borderRadius: 8 }}>{error}</div>
            )}
            {success && (
              <div style={{ background: '#efe', border: '1px solid #cfc', color: '#166534', padding: 12, borderRadius: 8 }}>{success}</div>
            )}

            {/* Profile image - same layout as CallCenter */}
            <div style={{ border: '1px solid #eef2f7', borderRadius: 16, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Fotografia de Perfil</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr', gap: 16, alignItems: 'center' }}>
                <div style={{ width: 96, height: 96, borderRadius: '50%', overflow: 'hidden', border: '2px solid #e5e7eb', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {form.profileImagePreview ? (
                    <img src={form.profileImagePreview} alt="Pré-visualização" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: 12 }}>Sem foto</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input id="profile-photo-input-comercial" type="file" accept="image/*" onChange={handleImageChange} style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }} />
                  <label htmlFor="profile-photo-input-comercial" style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f3f4f6', cursor: 'pointer', fontWeight: 600, color: '#111827' }}>Escolher Ficheiro</label>
                  {form.profileImageFile && (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{form.profileImageFile.name}</span>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Formatos suportados: JPG, PNG. Tamanho recomendado 400x400.</div>
            </div>

            {/* Personal info - mirrors CallCenter, adds Team */}
            <div style={{ border: '1px solid #eef2f7', borderRadius: 16, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Informações Pessoais</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Nome <span style={{ color: '#e11d48' }}>*</span></label>
                  <input name="name" value={form.name} onChange={handleChange} required placeholder="Nome" style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Email <span style={{ color: '#e11d48' }}>*</span></label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} required placeholder="email@wondertrade.pt" style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Contacto Telefónico <span style={{ color: '#e11d48' }}>*</span></label>
                  <input name="phone" value={form.phone} onChange={handleChange} required placeholder="+351 912 345 678" style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Função <span style={{ color: '#e11d48' }}>*</span></label>
                  <select name="role" value={form.role} onChange={handleChange} required style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }}>
                    <option value="">Selecione...</option>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Equipa <span style={{ color: '#e11d48' }}>*</span></label>
                  <select
                    name="team"
                    value={form.team || ''}
                    onChange={handleChange}
                    required
                    disabled={Boolean(forcedTeam)}
                    style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }}
                  >
                    {!forcedTeam && <option value="">Selecione...</option>}
                    {teamOptions.map((team) => (
                      <option key={team} value={team}>
                        {team}
                      </option>
                    ))}
                  </select>
                </div>
                {!hideCodigoComercial && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Código Comercial</label>
                  <input name="codigoComercial" value={form.codigoComercial || ''} onChange={handleChange} placeholder="Código Comercial" style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
                </div>
                )}
              </div>
            </div>

            {/* Emergency contact */}
            <div style={{ border: '1px solid #eef2f7', borderRadius: 16, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Contacto de Emergência</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Contacto</label>
                  <input name="emergencyContact" value={form.emergencyContact || ''} onChange={handleChange} placeholder="+351 9xx xxx xxx" style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label>Relação</label>
                  <input name="emergencyRelation" value={form.emergencyRelation || ''} onChange={handleChange} placeholder="Ex.: Mãe, Irmão" style={{ padding: 12, border: '2px solid #e5e7eb', borderRadius: 10 }} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 4 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={onClose} disabled={loading} style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f3f4f6', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={loading} style={{ padding: '10px 16px', borderRadius: 10, border: 0, background: '#dc2626', color: '#fff', cursor: 'pointer' }}>{loading ? 'A criar...' : 'Criar Utilizador'}</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ComercialCreateUserModal;


